import { Link } from 'react-router-dom'
import type { AlertType } from '../types'
import fuelTruck from '@assets/CustomIcons/fuel-truck.svg'
import { BgColorsOutlined, DollarCircleFilled } from '@ant-design/icons'
import { ControlPanelAlert } from './ControlPanelAlert'
import { GraviButton } from '@gravitate-js/excalibrr'

type AlertMessageProps = {
  alert: AlertType
  handleResolve: () => void
  scopes?: Partial<Gravitate.AuthorizationResponse>
}

export function TrailerSwapAlert({
  alert,
  handleViewTrailerSwap,
  handleResolve,
  scopes,
}: AlertMessageProps & { handleViewTrailerSwap: () => void }) {
  return (
    <ControlPanelAlert
      alert={alert}
      icon={<img src={fuelTruck} alt='Trailer Swap' style={{ width: '16px', height: '16px' }} />}
      actions={<GraviButton buttonText={'View Changes'} onClick={handleViewTrailerSwap} />}
      handleResolve={handleResolve}
      hideButtons={!scopes?.order_management}
    >
      Driver <b>{alert.payload.driver}</b> logged in with a {alert.payload.new_trailer_config} trailer.{' '}
      {alert.payload.orders.length} Order(s) have been modified to fit {alert.payload.new_trailer_config}.
    </ControlPanelAlert>
  )
}

export function PotentialMisdropAlert({ alert, handleResolve, scopes }: AlertMessageProps) {
  return (
    <ControlPanelAlert
      alert={alert}
      icon={<BgColorsOutlined />}
      actions={
        <GraviButton
          buttonText={'View Tank'}
          onClick={() => window.open(`/site_monitoring/${alert.payload.store_id}`, '_blank')}
        />
      }
      handleResolve={handleResolve}
      hideButtons={!scopes?.supply_directive?.price_management?.view}
    >
      Driver <b>{alert.payload.driver_name}</b> - <b>Order {alert.payload.order_number}</b> <br />
      Unable to confirm a drop of <b>{alert.payload.product}</b> at{' '}
      <b>
        Site {alert.payload.store_number}, {alert.payload.city}
      </b>
    </ControlPanelAlert>
  )
}

export function WrongLocationAlert({ alert, handleResolve }: AlertMessageProps) {
  return (
    <ControlPanelAlert alert={alert} handleResolve={handleResolve}>
      Driver <b>{alert.payload.driver_name}</b> - <b>Order {alert.payload.order_number}</b> <br />
      Driver expected at <b>{alert.payload.expected_location_name}</b>, but{' '}
      {alert.payload.estimated_location_name ? (
        <>
          was actually at <b>{alert.payload.estimated_location_name}</b>.
        </>
      ) : (
        'was unable to be located.'
      )}
    </ControlPanelAlert>
  )
}

export function SupplyNeedsAttentionAlert({ alert, handleResolve, scopes }: AlertMessageProps) {
  return (
    <ControlPanelAlert
      alert={alert}
      icon={<DollarCircleFilled />}
      actions={
        <GraviButton
          buttonText={'View Price'}
          onClick={() =>
            window.open(
              `/supply_directive/price_management?${new URLSearchParams({
                terminal: alert.payload.terminal,
                price_type: alert.payload.price_type,
                product: alert.payload.product,
                supplier: alert.payload.supplier,
              })}`,
              '_blank'
            )
          }
        />
      }
      handleResolve={handleResolve}
      hideButtons={!scopes?.supply_directive?.price_management?.view}
    >
      Driver <b>{alert.payload.driver_name}</b> has marked{' '}
      <Link
        target='_blank'
        to={`/supply_directive/price_management?${new URLSearchParams({
          terminal: alert.payload.terminal,
          price_type: alert.payload.price_type,
          product: alert.payload.product,
          supplier: alert.payload.supplier,
        })}`}
        style={{ textTransform: 'capitalize' }}
      >
        {alert.payload.terminal} - {alert.payload.product} - {alert.payload.supplier} on {alert.payload.price_type}{' '}
        {alert.payload.contract || ''}
      </Link>
      as unavailable. The supply option on the order was out of the auto-supply criteria.
    </ControlPanelAlert>
  )
}

export function CarrierOrderRejectAlert({ alert, handleResolve, scopes }: AlertMessageProps) {
  return (
    <ControlPanelAlert
      alert={alert}
      actions={
        <GraviButton
          buttonText={'View Order'}
          onClick={() =>
            window.open(
              `/order_management/carrier-dispatch?${new URLSearchParams({
                order_number: alert.payload.order_number,
              })}`,
              '_blank'
            )
          }
        />
      }
      handleResolve={handleResolve}
      hideButtons={!scopes?.supply_directive?.price_management?.view}
    >
      <b>{alert.payload.carrier_name}</b> rejected order #{' '}
      <Link
        target='_blank'
        to={`/order_management/carrier-dispatch?${new URLSearchParams({
          order_number: alert.payload.order_number,
        })}`}
        style={{ textTransform: 'capitalize' }}
      >
        {alert.payload.order_number}
      </Link>{' '}
      <br />
      <b>{`because ${alert.payload.reason_code}`}</b>
      {alert.payload.reason ? (
        <>
          <br />
          {`with note: "${alert.payload.reason}"`}
        </>
      ) : null}
    </ControlPanelAlert>
  )
}
